/**
 * Consolidated User Tracking Hook
 * Single hook that provides all tracking functionality:
 * - Page tracking
 * - Activity tracking  
 * - Session management
 * - Real-time data
 */

"use client";

import { useState, useEffect, useCallback } from 'react';
import { userTracking, TrackingData, UserActivity, PageVisit, UserInteraction } from '../utils/userTracking';

// Minimal session data for the hook (subset of SSOSessionData)
export interface MinimalSessionData {
  sessionId?: string;
  user_id?: string;
  subscriber_id?: number;
}

export interface UseUserTrackingReturn {
  // Current data
  currentPage: PageVisit | null;
  currentPageTime: number;
  currentPageVisitNumber: string;
  totalPageVisits: number;
  userActivity: UserActivity | null;
  trackingData: TrackingData;
  interactions: UserInteraction[];
  
  // Session data
  session: MinimalSessionData | null;
  isLoggedIn: boolean;
  sessionValid: boolean;
  
  // Actions
  trackPageEntry: (url?: string, title?: string) => void;
  trackUserAction: (actionType: string, element?: HTMLElement, coordinates?: { x: number; y: number }, metadata?: Record<string, any>) => void;
  forceUpdate: () => void;
  forcePageTracking: () => void;
  formatTimeAsObject: (milliseconds: number) => { m: number; s: number };
  
  // Loading states
  isLoading: boolean;
  error: string | null;
}

export function useUserTracking(): UseUserTrackingReturn {
  
  // Get session data from session manager
  const session = userTracking.getCurrentPage()?.sessionId ? {
    sessionId: userTracking.getCurrentPage()?.sessionId,
    user_id: userTracking.getCurrentPage()?.user_id,
    subscriber_id: userTracking.getCurrentPage()?.subscriber_id
  } : null;
  const isLoggedIn = !!session?.user_id;
  const sessionValid = !!session;
  
  // State
  const [currentPage, setCurrentPage] = useState<PageVisit | null>(null);
  const [currentPageTime, setCurrentPageTime] = useState<number>(0);
  const [currentPageVisitNumber, setCurrentPageVisitNumber] = useState<string>('');
  const [totalPageVisits, setTotalPageVisits] = useState<number>(0);
  const [userActivity, setUserActivity] = useState<UserActivity | null>(null);
  const [trackingData, setTrackingData] = useState<TrackingData>({
    pageVisits: [],
    userActivity: null,
    pageAnalytics: [],
    sessionSummary: {
      totalPages: 0,
      totalTimeSpent: 0,
      averageTimePerPage: 0,
      mostVisitedPage: '',
      engagementScore: 0
    },
    timestamp: Date.now()
  });
  const [interactions, setInteractions] = useState<UserInteraction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize tracking
  useEffect(() => {
    try {
      // Track initial page
      userTracking.trackPageEntry();
      
      // Get initial data
      setCurrentPage(userTracking.getCurrentPage());
      setCurrentPageTime(userTracking.getCurrentPageTime());
      setCurrentPageVisitNumber(userTracking.getCurrentPageVisitNumber());
      setTotalPageVisits(userTracking.getTotalPageVisits());
      setUserActivity(userTracking.getUserActivity());
      setTrackingData(userTracking.getTrackingData());
      setInteractions(userTracking.getInteractions());
      
      setIsLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize tracking');
      setIsLoading(false);
    }
  }, []);

  // Track page changes (App Router doesn't have router.events)
  useEffect(() => {
    // For App Router, we'll track page changes via pathname changes
    const handleRouteChange = () => {
      try {
        userTracking.trackPageEntry();
        setCurrentPage(userTracking.getCurrentPage());
        setCurrentPageTime(userTracking.getCurrentPageTime());
        setCurrentPageVisitNumber(userTracking.getCurrentPageVisitNumber());
        setTotalPageVisits(userTracking.getTotalPageVisits());
        setUserActivity(userTracking.getUserActivity());
        setTrackingData(userTracking.getTrackingData());
      } catch (err) {
        console.error('Error tracking page change:', err);
      }
    };

    // Track initial page load
    handleRouteChange();

    // Listen for browser navigation events (back/forward)
    const handlePopState = () => {
      setTimeout(handleRouteChange, 100); // Small delay to ensure DOM is updated
    };

    // Listen for navigation events
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Periodic data updates
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState !== "visible") {
        return;
      }
      try {
        setCurrentPage(userTracking.getCurrentPage());
        setCurrentPageTime(userTracking.getCurrentPageTime());
        setCurrentPageVisitNumber(userTracking.getCurrentPageVisitNumber());
        setTotalPageVisits(userTracking.getTotalPageVisits());
        setUserActivity(userTracking.getUserActivity());
        setTrackingData(userTracking.getTrackingData());
        setInteractions(userTracking.getInteractions());
      } catch (err) {
        console.error('Error updating tracking data:', err);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  // Update data when session changes (with proper dependency management)
  useEffect(() => {
    if (session) {
      try {
        userTracking.forceUpdateActivityData();
        const newUserActivity = userTracking.getUserActivity();
        const newTrackingData = userTracking.getTrackingData();
        
        // Only update state if data has actually changed
        setUserActivity(prev => {
          if (JSON.stringify(prev) !== JSON.stringify(newUserActivity)) {
            return newUserActivity;
          }
          return prev;
        });
        
        setTrackingData(prev => {
          if (JSON.stringify(prev) !== JSON.stringify(newTrackingData)) {
            return newTrackingData;
          }
          return prev;
        });
      } catch (err) {
        console.error('Error updating data after session change:', err);
      }
    }
  }, [session?.sessionId, session?.user_id, isLoggedIn, sessionValid]);

  // Actions
  const trackPageEntry = useCallback((url?: string, title?: string) => {
    try {
      userTracking.trackPageEntry(url, title);
      setCurrentPage(userTracking.getCurrentPage());
      setCurrentPageTime(userTracking.getCurrentPageTime());
      setCurrentPageVisitNumber(userTracking.getCurrentPageVisitNumber());
      setTotalPageVisits(userTracking.getTotalPageVisits());
      setUserActivity(userTracking.getUserActivity());
      setTrackingData(userTracking.getTrackingData());
    } catch (err) {
      console.error('Error tracking page entry:', err);
    }
  }, []);

  const trackUserAction = useCallback((actionType: string, element?: HTMLElement, coordinates?: { x: number; y: number }, metadata?: Record<string, any>) => {
    try {
      userTracking.trackUserAction(actionType, element, coordinates, metadata);
      setCurrentPage(userTracking.getCurrentPage());
      setCurrentPageTime(userTracking.getCurrentPageTime());
      setCurrentPageVisitNumber(userTracking.getCurrentPageVisitNumber());
      setTotalPageVisits(userTracking.getTotalPageVisits());
      setUserActivity(userTracking.getUserActivity());
      setTrackingData(userTracking.getTrackingData());
      setInteractions(userTracking.getInteractions());
    } catch (err) {
      console.error('Error tracking user action:', err);
    }
  }, []);

  const forceUpdate = useCallback(() => {
    try {
      userTracking.forceUpdateActivityData();
      setCurrentPage(userTracking.getCurrentPage());
      setCurrentPageTime(userTracking.getCurrentPageTime());
      setCurrentPageVisitNumber(userTracking.getCurrentPageVisitNumber());
      setTotalPageVisits(userTracking.getTotalPageVisits());
      setUserActivity(userTracking.getUserActivity());
      setTrackingData(userTracking.getTrackingData());
      setInteractions(userTracking.getInteractions());
    } catch (err) {
      console.error('Error force updating tracking data:', err);
    }
  }, []);

  const forcePageTracking = useCallback(() => {
    try {
      userTracking.forcePageTracking();
      setCurrentPage(userTracking.getCurrentPage());
      setCurrentPageTime(userTracking.getCurrentPageTime());
      setCurrentPageVisitNumber(userTracking.getCurrentPageVisitNumber());
      setTotalPageVisits(userTracking.getTotalPageVisits());
      setUserActivity(userTracking.getUserActivity());
      setTrackingData(userTracking.getTrackingData());
    } catch (err) {
      console.error('Error force tracking page:', err);
    }
  }, []);

  const formatTimeAsObject = useCallback((milliseconds: number) => {
    return userTracking.formatTimeAsObject(milliseconds);
  }, []);

  return {
    // Current data
    currentPage,
    currentPageTime,
    currentPageVisitNumber,
    totalPageVisits,
    userActivity,
    trackingData,
    interactions,
    
    // Session data
    session,
    isLoggedIn,
    sessionValid,
    
    // Actions
    trackPageEntry,
    trackUserAction,
    forceUpdate,
    forcePageTracking,
    formatTimeAsObject,
    
    // Loading states
    isLoading,
    error
  };
}

// ============================================================================
// SIMPLIFIED HOOKS FOR SPECIFIC USE CASES
// ============================================================================

/**
 * Hook for just page tracking
 */
export function usePageTracking() {
  const { currentPage, trackPageEntry, trackingData } = useUserTracking();
  
  return {
    currentPage,
    trackPageEntry,
    pageVisits: trackingData.pageVisits,
    pageAnalytics: trackingData.pageAnalytics
  };
}

/**
 * Hook for just activity tracking
 */
export function useActivityTracking() {
  const { interactions, trackUserAction, userActivity } = useUserTracking();
  
  return {
    interactions,
    trackUserAction,
    userActivity
  };
}

/**
 * Hook for session management only
 */
export function useSession() {
  const { session, isLoggedIn, sessionValid } = useUserTracking();
  
  return {
    session,
    isLoggedIn,
    sessionValid
  };
}
